import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useCurrency } from '../store/currency';
import { format } from 'date-fns';



export default function TransactionsPage() {
  const { fmt } = useCurrency();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ year: new Date().getFullYear(), month: '', category_id: '', uncategorized: false });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [txs, cats] = await Promise.all([
        api.getTransactions({
          year: filters.year || undefined,
          month: filters.month || undefined,
          category_id: filters.category_id || undefined,
          uncategorized: filters.uncategorized || undefined,
        }),
        api.getCategories()
      ]);
      setTransactions(txs);
      setCategories(cats);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters]);

  const updateCategory = async (txId, catId) => {
    try {
      const updated = await api.updateTransaction(txId, { category_id: parseInt(catId) });
      setTransactions(txs => txs.map(t => t.id === txId ? { ...t, ...updated } : t));
      setEditingId(null);
      toast.success('Category updated');
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteTransaction = async (txId) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await api.deleteTransaction(txId);
      setTransactions(txs => txs.filter(t => t.id !== txId));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const totalExpenses = transactions.filter(t => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
  const totalIncome = transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);

  return (
    <div className="animate-in">
      <div className="page-title">Transactions</div>
      <div className="page-subtitle">{transactions.length} transactions loaded</div>

      {/* Filters */}
      <div className="card card-sm mb-6" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div className="form-label">Year</div>
          <select className="input" style={{ width: 100 }} value={filters.year} onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}>
            {[2023, 2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div className="form-label">Month</div>
          <select className="input" style={{ width: 110 }} value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}>
            <option value="">All months</option>
            {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <div className="form-label">Category</div>
          <select className="input" style={{ width: 160 }} value={filters.category_id} onChange={e => setFilters(f => ({ ...f, category_id: e.target.value }))}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13 }}>
          <input type="checkbox" checked={filters.uncategorized} onChange={e => setFilters(f => ({ ...f, uncategorized: e.target.checked }))} />
          Show uncategorized only
        </label>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 20 }}>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-label">Income</div>
            <div style={{ color: 'var(--green)', fontWeight: 600 }}>${totalIncome.toFixed(0)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-label">Expenses</div>
            <div style={{ color: 'var(--red)', fontWeight: 600 }}>${totalExpenses.toFixed(0)}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No transactions found</td></tr>
              ) : transactions.map(tx => (
                <tr key={tx.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {format(new Date(tx.date), 'MMM d')}
                  </td>
                  <td>
                    <div style={{ fontSize: 13.5 }}>{tx.description}</div>
                    {tx.ai_suggested_category && !tx.manually_categorized && (
                      <div style={{ fontSize: 11, color: 'var(--accent)', opacity: 0.7, marginTop: 2 }}>AI: {tx.ai_suggested_category}</div>
                    )}
                  </td>
                  <td>
                    {editingId === tx.id ? (
                      <select
                        className="input"
                        style={{ width: 160, padding: '4px 8px', fontSize: 12 }}
                        value={tx.category_id || ''}
                        onChange={e => updateCategory(tx.id, e.target.value)}
                        autoFocus
                        onBlur={() => setEditingId(null)}
                      >
                        <option value="">Uncategorized</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                      </select>
                    ) : (
                      <button
                        onClick={() => setEditingId(tx.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        {tx.category_name ? (
                          <span className="cat-pill" style={{ borderLeft: `3px solid ${tx.category_color}` }}>
                            {tx.category_icon} {tx.category_name}
                          </span>
                        ) : (
                          <span className="badge badge-yellow">Uncategorized</span>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="text-right">
                    <span className={tx.amount >= 0 ? 'amount-pos' : 'amount-neg'}>
                      {tx.amount >= 0 ? '+' : '-'}{fmt(tx.amount)}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteTransaction(tx.id)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
